import { useMutation, useQueryClient } from "@tanstack/react-query";
import { KeysysToastProps } from "../component-library";
import { useToasts } from "../contexts";
import { getErrorWithMessage } from "../helper-functions";
import { GenericMutationOptions, MutationResult } from "../models";
import { useCallback } from "react";

export const useGenericMutation = <TData, TError, TVariables, TContext = TData>(
    options: GenericMutationOptions<TData, TError, TVariables, TContext>
): MutationResult<TData, TError, TVariables, TContext> => {
    const {
        mutationFn,
        queryKey,
        invalidateQueryKey,
        keysToInvalidate,
        updater,
        disableSuccessToast,
        successToast,
        disableErrorToast,
        errorToast,
        onError,
        onMutate,
        onSettled,
        onSuccess,
    } = options;

    const queryClient = useQueryClient();
    const { showToast } = useToasts();

    const _onMutate = useCallback(
        async (variables: TVariables) => {
            if (onMutate) {
                return onMutate(variables);
            }
            if (!updater) return undefined;

            // cancel outgoing queries
            await queryClient.cancelQueries(queryKey);

            // grab prev data for fallback
            const prevData = queryClient.getQueryData<TData>(queryKey);

            // optimistic update
            queryClient.setQueryData<TData>(queryKey, (prevData) => updater(prevData, variables));

            // return prev data as context for other callbacks
            return prevData as TContext;
        },
        [onMutate, updater, queryClient, queryKey]
    );

    const _onSuccess = useCallback(
        (data: TData, variables: TVariables, context: TContext | undefined) => {
            if (onSuccess) {
                return onSuccess(data, variables, context);
            }
            if (disableSuccessToast === true) return;
            let toast: KeysysToastProps = {
                name: "MUTATION_SUCCESS",
                titleInHeader: "Success",
                body: "Your action was successful.",
                theme: "success",
            };
            if (successToast) {
                if (typeof successToast === "string") {
                    toast.body = successToast;
                } else {
                    toast = successToast;
                }
            }
            showToast(toast);
        },
        [onSuccess, disableSuccessToast, successToast, showToast]
    );

    const _onError = useCallback(
        (error: TError, variables: TVariables, context: TContext | undefined) => {
            if (onError) {
                return onError(error, variables, context);
            }

            if (updater) {
                // fallback to prev data
                queryClient.setQueryData(queryKey, context);
            }

            if (disableErrorToast === true) return;
            const { message } = getErrorWithMessage(error);
            let toast: KeysysToastProps = {
                name: "MUTATION_ERROR",
                titleInHeader: "Error",
                body: "Your action was unsuccessful.\n" + message,
                theme: "danger",
                delay: 5000,
            };
            if (errorToast) {
                if (typeof errorToast === "string") {
                    toast.body = errorToast;
                } else {
                    toast = errorToast;
                }
            }
            showToast(toast);
        },
        [onError, updater, queryClient, queryKey, disableErrorToast, errorToast, showToast]
    );

    const _onSettled = useCallback(
        (data: TData | undefined, error: TError | null, variables: TVariables, context: TContext | undefined) => {
            if (onSettled) {
                return onSettled(data, error, variables, context);
            }
            const keys = [];
            if (invalidateQueryKey !== false) {
                keys.push(queryKey);
            }
            if (keysToInvalidate?.length) {
                keys.push(...keysToInvalidate);
            }

            if (keys.length) {
                return Promise.all(keys.map((k) => queryClient.invalidateQueries(k)));
            }
        },
        [onSettled, invalidateQueryKey, queryKey, keysToInvalidate, queryClient]
    );

    const { mutate, mutateAsync, isError, isLoading, isSuccess } = useMutation<TData, TError, TVariables, TContext>(
        mutationFn,
        {
            onMutate: _onMutate,
            onSuccess: _onSuccess,
            onError: _onError,
            onSettled: _onSettled,
        }
    );

    return { mutate, mutateAsync, isError, isLoading, isSuccess };
};
